<template>
  <v-dialog v-model="visible" fullscreen transition="dialog-bottom-transition">
    <v-card>
      <!-- Main Toolbar -->
      <v-toolbar flat color="#AC1726" dark>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Available and Taken Bins</v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <!-- Available Bins Section -->
        <v-row>
          <v-col cols="12">
            <h3 class="mt-4 mb-2 available-bins-header">Available Bins</h3>
          </v-col>
          <v-col cols="12">
            <div class="box-grid">
              <v-btn
                v-for="box in availableBins"
                :key="'available-' + box.number"
                color="green lighten-4"
                class="box-button available-bin"
                elevation="2"
              >
                <div class="box-content">
                  <span class="box-number">{{ box.number }}</span>
                  <v-icon
                    class="bin-icon"
                    color="green darken-1"
                  >
                    mdi-check-circle
                  </v-icon>
                </div>
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!-- Taken Bins Section -->
        <v-row>
          <v-col cols="12">
            <h3 class="mt-4 mb-2 taken-bins-header">Taken Bins</h3>
          </v-col>
          <v-col cols="12">
            <div class="box-grid">
              <v-btn
                v-for="box in takenBins"
                :key="'taken-' + box.number"
                color="red lighten-4"
                class="box-button taken-bin"
                elevation="2"
                @click="showBinDetails(box.number)"
              >
                <div class="box-content">
                  <span class="box-number">{{ box.number }}</span>
                  <v-icon
                    class="bin-icon"
                    color="red darken-1"
                  >
                    mdi-close-circle
                  </v-icon>
                </div>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="#AC1726" text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Enhanced Bin Details Dialog -->
    <v-dialog v-model="detailsDialog.visible" max-width="700px">
      <v-card>
        <!-- Details Toolbar -->
        <v-toolbar color="#AC1726" dark>
          <v-btn icon @click="detailsDialog.visible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Bin Details</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <v-card-text>
          <v-skeleton-loader v-if="detailsLoading" type="card"></v-skeleton-loader>
          <div v-else>
            <v-container fluid>
              <!-- Bin Information Section -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-card flat>
                    <v-card-title>
                      <v-icon class="mr-2" color="primary">mdi-bin</v-icon>
                      Bin Information
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="6">
                          <strong>Spares ID:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.sparesId }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Status:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.status }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Bin Number:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.binNumber }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Dates Section -->
                <v-col cols="12" md="6">
                  <v-card flat>
                    <v-card-title>
                      <v-icon class="mr-2" color="primary">mdi-calendar</v-icon>
                      Dates
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="6">
                          <strong>Created Date:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ formatDate(binDetails.createdDate) }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Projected Date:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ formatDate(binDetails.projectedDate) }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Details Section: Reason, Quantity, Comment, Journal Number, Code -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-card flat>
                    <v-card-title>
                      <v-icon class="mr-2" color="primary">mdi-information</v-icon>
                      Details
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="6">
                          <strong>Reason:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.reason }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Quantity:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.qty }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Comment:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.comment }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Journal Number:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.journalNumber }}
                        </v-col>

                        <v-col cols="6">
                          <strong>Code:</strong>
                        </v-col>
                        <v-col cols="6">
                          {{ binDetails.code }}
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Image Section -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-card flat>
                    <v-card-title>
                      <v-icon class="mr-2" color="primary">mdi-image</v-icon>
                      Image
                    </v-card-title>
                    <v-card-text>
                      <v-img
                        v-if="binDetails.imageUrl"
                        :src="binDetails.imageUrl"
                        max-width="100%"
                        class="mt-2"
                        contain
                      ></v-img>
                      <div v-else class="mt-2">No Image Available</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#AC1726" text @click="detailsDialog.visible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { firebaseApp } from "@/firebaseConfig"; // Adjust the path as needed

const db = getFirestore(firebaseApp);

export default {
  name: "BinsDialog",
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    availableBins: {
      type: Array,
      required: true,
    },
    takenBins: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      detailsDialog: {
        visible: false,
      },
      binDetails: {},
      detailsLoading: false,
      errorSnackbar: false,
      errorMessage: "",
    };
  },
  methods: {
    /**
     * Emits an event to update the visibility of the main dialog.
     */
    close() {
      this.$emit("update:visible", false);
    },

    /**
     * Formats a date string into a readable format.
     * @param {string|Date} date - The date to format.
     * @returns {string} - Formatted date string.
     */
    formatDate(date) {
      if (!date) return "N/A";
      const d = new Date(date);
      if (isNaN(d)) return "N/A";
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    /**
     * Fetches and displays the details of the selected bin.
     * Only fetches details if the bin's status is not 'Completed'.
     * @param {string|number} binNumber - The number of the bin clicked.
     */
    async showBinDetails(binNumber) {
      this.detailsDialog.visible = true;
      this.detailsLoading = true;
      this.binDetails = {};

      try {
        const sparesCollection = collection(db, "AwaitingSpares");

        // **Option 1:** Use 'in' operator to avoid '!=' and potential indexing issues
        const q = query(
          sparesCollection,
          where("binNumber", "==", parseInt(binNumber, 10)),
          where("status", "in", ["Factory Repair", "Local Repair"])
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // No details found for the bin
          this.binDetails = {
            sparesId: "N/A",
            status: "N/A",
            journalNumber: "N/A",
            binNumber: binNumber,
            createdDate: "N/A",
            projectedDate: "N/A",
            reason: "N/A",
            qty: "N/A",
            code: "N/A",
            imageUrl: "",
            comment: "N/A",
          };
        } else {
          // Assuming one spare per bin
          const spare = querySnapshot.docs[0].data();
          this.binDetails = {
            sparesId: spare.sparesId || "N/A",
            status: spare.status || "N/A",
            journalNumber: spare.journalNumber || "N/A",
            binNumber: spare.binNumber || "N/A",
            createdDate: spare.createdDate || "N/A",
            projectedDate: spare.projectedDate || "N/A",
            reason: spare.reason || "N/A",
            qty: spare.qty || "N/A",
            code: spare.code || "N/A",
            imageUrl: spare.imageUrl || "",
            comment: spare.comment || "N/A",
          };
        }
      } catch (error) {
        console.error("Error fetching bin details:", error);
        this.errorMessage = "Failed to fetch bin details.";
        this.errorSnackbar = true;
      } finally {
        this.detailsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Box Grid Styling */
.box-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); /* Increased min width for larger bins */
  gap: 24px; /* Increased spacing between boxes */
  margin-bottom: 40px; /* Increased bottom margin */
}

/* Box Button Styling */
.box-button {
  width: 100%;
  aspect-ratio: 1 / 1; /* Ensures the box is square */
  position: relative;
  border-radius: 16px; /* Further increased border radius */
  background-color: white !important;
  color: black !important;
  overflow: hidden;
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15); /* Enhanced shadow */
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.box-button:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 16px rgba(0, 0, 0, 0.2); /* Enhanced hover shadow */
}

/* Inner Content Styling */
.box-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Box Number Styling */
.box-number {
  font-size: 24px; /* Increased font size */
  font-weight: bold;
}

/* Bin Icon Styling */
.bin-icon {
  display: flex;
  align-items: center;
  margin-left: 12px; /* Further increased spacing */
  font-size: 2rem; /* Further increased icon size */
}

/* Available Bins Header Styling */
.available-bins-header {
  color: #28a745; /* Green color */
  font-size: 1.5rem; /* Increased header font size */
}

/* Taken Bins Header Styling */
.taken-bins-header {
  color: #dc3545; /* Red color */
  font-size: 1.5rem; /* Increased header font size */
}

/* Toolbar Styling */
.v-toolbar {
  border-bottom: 1px solid #ddd;
}

.v-toolbar-title {
  font-weight: 700;
  font-size: 1.5rem;
}

@media (max-width: 768px) {
  .v-toolbar-title {
    font-size: 1.2rem;
  }
}

/* Container Styling */
.v-card-text {
  padding: 24px;
}

@media (max-width: 768px) {
  .v-card-text {
    padding: 16px;
  }
}

@media (max-width: 576px) {
  .v-card-text {
    padding: 12px;
  }
}

/* Responsive Adjustments */
@media (max-width: 1200px) {
  .box-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 20px;
  }

  .box-number {
    font-size: 22px;
  }

  .bin-icon {
    font-size: 1.8rem;
  }
}

@media (max-width: 992px) {
  .box-grid {
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 18px;
  }

  .box-number {
    font-size: 20px;
  }

  .bin-icon {
    font-size: 1.6rem;
  }
}

@media (max-width: 768px) {
  .box-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 16px;
  }

  .box-number {
    font-size: 18px;
  }

  .bin-icon {
    font-size: 1.4rem;
  }
}

@media (max-width: 576px) {
  .box-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 14px;
  }

  .box-number {
    font-size: 16px;
  }

  .bin-icon {
    font-size: 1.2rem;
  }
}

/* Details Dialog Styling */
.v-dialog__content {
  display: flex;
  align-items: center;
  justify-content: center;
}

.v-skeleton-loader {
  width: 100%;
}

/* Snackbar Styling */
.v-snackbar {
  font-family: "Montserrat", sans-serif;
}

/* Additional Styling for Details Sections */
.v-card-title {
  font-size: 1.2rem;
}

.v-card-title .v-icon {
  font-size: 1.5rem;
}

.v-card-text strong {
  color: #555;
}

.v-card-text div {
  margin-bottom: 8px;
}

/* Responsive Image */
.v-img {
  border-radius: 8px;
}
</style>
